import type { ReactNode, CSSProperties } from 'react';

interface CardProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'surface' | 'elevated' | 'dark';
  hover?: boolean;
  style?: CSSProperties;
}

const variantStyles = {
  default: 'bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)]',
  surface: 'bg-[var(--asi-surface)]',
  elevated: 'bg-[var(--asi-bg)] shadow-[var(--shadow-md)]',
  dark: 'bg-[var(--asi-primary)] text-[var(--asi-text-light)]',
};

export function Card({ children, className = '', variant = 'default', hover = false, style }: CardProps) {
  return (
    <div
      className={`rounded-2xl transition-all duration-200 ${variantStyles[variant]} ${
        hover ? 'hover:shadow-[var(--shadow-lg)] hover:-translate-y-0.5' : ''
      } ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  children: ReactNode;
  className?: string;
  border?: boolean;
}

export function CardHeader({ children, className = '', border = true }: CardHeaderProps) {
  return (
    <div
      className={`px-6 py-4 ${
        border ? 'border-b border-[var(--asi-bg-tertiary)]' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
}

interface CardTitleProps {
  children: ReactNode;
  className?: string;
  as?: 'h2' | 'h3' | 'h4';
}

export function CardTitle({ children, className = '', as: Tag = 'h3' }: CardTitleProps) {
  return (
    <Tag
      className={`text-lg font-semibold text-[var(--asi-text)] tracking-tight ${className}`}
    >
      {children}
    </Tag>
  );
}

interface CardDescriptionProps {
  children: ReactNode;
  className?: string;
}

export function CardDescription({ children, className = '' }: CardDescriptionProps) {
  return (
    <p className={`text-sm text-[var(--asi-text-secondary)] mt-1 ${className}`}>
      {children}
    </p>
  );
}

interface CardContentProps {
  children: ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export function CardContent({ children, className = '', padding = 'md' }: CardContentProps) {
  return <div className={`${paddingStyles[padding]} ${className}`}>{children}</div>;
}

interface CardFooterProps {
  children: ReactNode;
  className?: string;
  border?: boolean;
}

export function CardFooter({ children, className = '', border = true }: CardFooterProps) {
  return (
    <div
      className={`px-6 py-4 ${
        border ? 'border-t border-[var(--asi-bg-tertiary)]' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
}
